import {
  type CreateReference,
  TaskCode,
  TaskReferenceType,
} from '@quromedical/fhir-common';
import { z } from 'zod';
import { DisplayReferenceSchema, UUIDSchema } from '../domain/models';
import { PaginationSchema } from '../domain/pagination';
import { dateSchema, PeriodSchema } from '../domain/schema';
import type { WithRequired } from '../types';

export const TaskStatusSchema = [
  'draft',
  'requested',
  'received',
  'accepted',
  'rejected',
  'ready',
  'cancelled',
  'in-progress',
  'on-hold',
  'failed',
  'completed',
  'entered-in-error',
] as const;
export type TaskStatus = fhir4.Task['status'];

export const TaskIntentSchema = [
  'unknown',
  'proposal',
  'plan',
  'order',
  'original-order',
  'reflex-order',
  'filler-order',
  'instance-order',
  'option',
] as const;
export type TaskIntent = fhir4.Task['intent'];

export const TaskNoteScheme = z.object({
  author: DisplayReferenceSchema,
  text: z.string(),
  time: z.string(),
});

export const TaskReferenceSchema = z.object({
  type: z.nativeEnum(TaskReferenceType),
  value: DisplayReferenceSchema,
});
/**
 * Represents a resource that is linked to a task for processing
 *
 * > Currently implements References but can be of any value type (e.g. string, date, base64, etc.)
 */
export type TaskReference = z.infer<typeof TaskReferenceSchema>;

export const TaskSchema = z.object({
  created: z.string(),
  updated: z.string(),
  status: z.enum(TaskStatusSchema),
  requester: DisplayReferenceSchema,
  intent: z.enum(TaskIntentSchema),
  description: z.string(),
  code: z.nativeEnum(TaskCode),
  admission: DisplayReferenceSchema,
  period: PeriodSchema,
  requestedPeriod: PeriodSchema,
  input: TaskReferenceSchema.array(),
  output: TaskReferenceSchema.array(),
  focus: DisplayReferenceSchema,
  for: DisplayReferenceSchema,
  notes: TaskNoteScheme.array(),
  owner: DisplayReferenceSchema,
  performers: DisplayReferenceSchema.array(),
});

export const PartialTaskSchema = TaskSchema.partial().extend({
  status: z.enum(TaskStatusSchema),
  intent: z.enum(TaskIntentSchema),
  notes: TaskNoteScheme.array(),
  input: TaskReferenceSchema.array(),
  output: TaskReferenceSchema.array(),
  performers: DisplayReferenceSchema.array(),
});

export const PartialTaskWithIdSchema = PartialTaskSchema.extend({
  id: UUIDSchema,
});

export type CreateTask = WithRequired<PartialTask, 'owner' | 'for' | 'created'>;

const taskCodeSchema = z.nativeEnum(TaskCode);

export const TaskAssignmentSchema = z.object({
  description: z.string(),
  performer: DisplayReferenceSchema,
  code: taskCodeSchema,
  requestedPeriodEnd: z.string().optional(),
});

export const TaskGetResponseSchema = PartialTaskWithIdSchema;

export const TaskGetAllResponseSchema = PaginationSchema.extend({
  tasks: PartialTaskWithIdSchema.array(),
});

/**
 * @deprecated Please use TaskGetAllResponse instead
 */
export type GetAllResponse = TaskGetAllResponse;

export interface TaskUpdateStatusRequest {
  status: TaskStatus;
  note: string;
}

export type TaskUpdateStatusResponse = PartialTaskWithId;

/**
 * Statuses that resolve to a group of statuses broadly into those that need action and those that don't
 */
export type ActivityStatus = 'active' | 'inactive';

export type DueStatus = 'overdue';

export type CustomTaskStatuses = ActivityStatus | DueStatus;

export type SearchStatus = TaskStatus | CustomTaskStatuses;

const status = [
  'draft',
  'requested',
  'received',
  'accepted',
  'rejected',
  'ready',
  'cancelled',
  'in-progress',
  'on-hold',
  'failed',
  'completed',
  'entered-in-error',
] as const;

const taskSearchStatus = [...status, 'active', 'inactive', 'overdue'] as const;

export const statusSchema = z.enum(status).describe('The state of the task');
export const TaskSearchStatusSchema = z
  .enum(taskSearchStatus)
  .describe('The state of the task');

export const BaseTaskSearchSchema = PaginationSchema.extend({
  code: z
    .nativeEnum(TaskCode)
    .optional()
    .describe('The type of task to search by'),
  encounter: z
    .string()
    .optional()
    .describe('Reference to the Encounter Resource'),
  status: TaskSearchStatusSchema.optional(),
});

export const BaseTaskUpdateSchema = z.object({
  id: z.string(),
  status: statusSchema,
  dueDate: dateSchema,
  note: z.string(),
});

export const TaskUpdateOverdueSchema = z.object({
  id: z.string(),
  dueDate: dateSchema,
  note: z.string(),
});

export const TaskUpdateStatusSchema = z.object({
  id: z.string(),
  status: statusSchema,
  note: z.string(),
});

export const TaskUpdateSchema = z.object({
  id: z.string(),
  status: z.enum(status).describe('The state of the task').optional(),
  dueDate: dateSchema.optional(),
  note: z.string(),
});

export const TaskCreateSchema = z.object({
  patientId: z.string().uuid(),
  description: z.string(),
  performer: DisplayReferenceSchema,
  code: z
    .nativeEnum(TaskCode)
    .optional()
    .describe('The type of task to search by'),
  requestedPeriodEnd: z.string().optional(),
  focus: DisplayReferenceSchema.optional(),
});

export const TaskSearchSchema = BaseTaskSearchSchema.extend({
  owner: z
    .string()
    .optional()
    .describe('Reference(Practitioner | Organization)'),
  subject: z.string().optional().describe('The subject of the task'),
  focus: z.string().optional().describe('What task is acting on'),
});

export const PractitionerTaskSearchSchema = BaseTaskSearchSchema.extend({
  owner: z
    .string()
    .optional()
    .describe('Reference(Practitioner | Organization)'),
});

export const PatientTaskSearchSchema = BaseTaskSearchSchema.extend({
  subject: z.string().describe('The subject of the task'),
  focus: z.string().optional().describe('What task is acting on'),
});

export type Task = z.infer<typeof TaskSchema>;
export type PartialTask = z.infer<typeof PartialTaskSchema>;
export type PartialTaskWithId = z.infer<typeof PartialTaskWithIdSchema>;
export type TaskNote = z.infer<typeof TaskNoteScheme>;
export type TaskGetResponse = z.infer<typeof TaskGetResponseSchema>;
export type TaskAssignment = z.infer<typeof TaskAssignmentSchema>;
export type TaskGetAllResponse = z.infer<typeof TaskGetAllResponseSchema>;
export type TaskSearchStatus = z.infer<typeof TaskSearchStatusSchema>;

export type TaskSearch = z.infer<typeof TaskSearchSchema>;
export type PractitionerTaskSearch = z.infer<
  typeof PractitionerTaskSearchSchema
>;
export type PatientTaskSearch = z.infer<typeof PatientTaskSearchSchema>;

export type TaskBaseUpdate = z.infer<typeof BaseTaskUpdateSchema>;
export type TaskUpdate = z.infer<typeof TaskUpdateSchema>;
export type TaskUpdateOverdue = z.infer<typeof TaskUpdateOverdueSchema>;
export type TaskUpdateStatus = z.infer<typeof TaskUpdateStatusSchema>;

export type TaskCreate = z.infer<typeof TaskCreateSchema>;

/**
 * @deprecated Please use TaskSearch instead
 */
export type GetAllParams = TaskSearch;

/**
 * @deprecated Please use PractitionerTaskSearch instead
 */
export type GetPractitionerParams = PractitionerTaskSearch;

/**
 * @deprecated Please use PatientTaskSearch instead
 */
export type GetPatientParams = PatientTaskSearch;

export interface CreateTaskAssignment extends TaskAssignment {
  focus?: CreateReference;
}
